import { AdditionalLogContext, PlayerInterface } from '@signage-player/contracts';
import { SignageApplication } from '@signage-player/core';
import { Device } from '@signage-player/data-store';
import { isObserverInstance, peekUrlDisplayId } from '@signage-player/device-browser';

/**
 * Browser flavor of ElectronSignageApplication. Strips Linux-specific paths
 * (network probes, AnyDesk file reads, log-cleanup.sh, NetworkManager restart,
 * offline-reboot escalation), keeps the platform-agnostic monitoring/offline
 * tracking. All values that came from `os`/`si`/`fs` are best-effort from
 * browser APIs.
 */
export class WebSignageApplication extends SignageApplication {
  public initialize(): void {
    super.initialize();
  }

  protected getAdditionalDeviceData(): Record<string, any> {
    return { ...super.getAdditionalDeviceData() };
  }

  protected shouldReportDeviceData(): boolean {
    return !isObserverInstance();
  }

  // Web binds a URL-chosen display, not necessarily displays[0] — relaunch
  // only when the bound display no longer exists on the device.
  protected shouldRelaunchForDisplayChange(device: Device): boolean {
    return (
      this.displayPlayerManagerInitializedOnDisplay !== null &&
      device.displays.length > 0 &&
      !device.displays.some(d => d.id === this.displayPlayerManagerInitializedOnDisplay)
    );
  }

  protected async updateLoggerAdditionalContext(): Promise<AdditionalLogContext['device'] | null> {
    try {
      const dm = (navigator as any).deviceMemory;
      const memory_size = typeof dm === 'number' ? dm * 1024 * 1024 * 1024 : 0;

      let storage_size = 0;
      let free_storage = 0;
      if (navigator.storage?.estimate) {
        const est = await navigator.storage.estimate();
        storage_size = est.quota ?? 0;
        free_storage = (est.quota ?? 0) - (est.usage ?? 0);
      }

      const deviceContext: AdditionalLogContext['device'] = {
        online: !this.isOffline(),
        low_memory: memory_size > 0 && memory_size < 1024 * 1024 * 1024,
        memory_size,
        usable_memory: memory_size, // browser doesn't distinguish used vs total
        device_unique_identifier: this.authInstance.hash,
        storage_size,
        free_storage,
      };
      this.logger.setAdditionalContext({
        device: deviceContext,
        platform: this.platform,
      });
      return deviceContext;
    } catch (error) {
      this.logger.error('[WebSignageApplication] Error updating logger additional context', { error: error as any });
      return null;
    }
  }

  protected logAppStatus(): void {
    super.logAppStatus();
    this.logger.notice('[WebSignageApplication] Periodic status report (browser)', {
      visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
    });
  }

  protected async monitoringLoop(): Promise<void> {
    try {
      await super.monitoringLoop();
    } catch (err) {
      this.logger.warning('[WebSignageApplication] Error during monitoring loop', { error: err as any });
    }
    // No offline-escalation reboot: WebLifecycle.reboot() is a no-op in the browser.
  }

  protected handleLowBandwidthModeChange(_enabled: boolean): void {
    // n/a in browser — no NetworkManager script to invoke.
  }

  protected async initializeDisplayPlayerManager(device: Device): Promise<void> {
    if (this.displayPlayerManagerInitializedOnDisplay === null && device.displays.length > 0) {
      const desired = peekUrlDisplayId();
      const targetDisplayId = device.displays.find(d => d.id === desired)?.id ?? device.displays[0].id;
      if (desired !== null && targetDisplayId !== desired) {
        this.logger.warning('[WebSignageApplication] Display from URL not found on device, using first display', {
          desired,
          available: device.displays.map(d => d.id),
        });
      }
      if (targetDisplayId === 0) {
        this.logger.critical('[WebSignageApplication] Server returned 0 display id, skipping display player manager initialization');
        return;
      }
      const ok = await this.displayPlayerManager.changeDisplay(0, targetDisplayId);
      if (!ok) {
        this.logger.error('[WebSignageApplication] Failed to change display player manager', { displayId: targetDisplayId });
        return;
      }
      this.displayPlayerManagerInitializedOnDisplay = targetDisplayId;
    }
  }

  protected async getPlayerForDisplay(displayId: number): Promise<PlayerInterface | null> {
    if (!this.displayPlayerManager.hasForDisplay(displayId)) return null;
    return await this.displayPlayerManager.getOrInitializeForDisplay(displayId);
  }
}

export default WebSignageApplication;
